import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { nutritionAPI } from '../../services/api'
import {
  XMarkIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'

/**
 * SaveAsFavoriteModal Component
 *
 * Modal for saving a meal as a favorite template.
 * User provides a custom name for the favorite.
 */
export default function SaveAsFavoriteModal({
  isOpen,
  mealId,
  mealType: _mealType,
  defaultName = '',
  onSave = () => {},
  onClose = () => {}
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [name, setName] = useState(defaultName)
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: () => nutritionAPI.saveAsFavorite(mealId, name, description || undefined),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['favoriteMeals'] })
      onSave(response.data)
      onClose()
    },
    onError: (err) => {
      const detail = err.response?.data?.detail || t('favorites.saveFailed', 'Failed to save favorite')
      setError(detail)
    }
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!name.trim()) {
      setError(t('favorites.nameRequired', 'Please enter a name for your favorite'))
      return
    }

    saveMutation.mutate()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center space-x-2">
            <StarIconSolid className="h-6 w-6 text-yellow-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t('favorites.saveAsFavorite', 'Save as Favorite')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            {t('favorites.saveDescription', 'Save this meal as a favorite so you can quickly add it again later.')}
          </p>

          {/* Name Input */}
          <div>
            <label htmlFor="favorite-name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('favorites.name', 'Name')} *
            </label>
            <input
              id="favorite-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('favorites.namePlaceholder', 'e.g., Weekday Breakfast')}
              className="w-full px-3 py-2 focus:ring-2"
              autoFocus
              maxLength={200}
            />
          </div>

          {/* Description Input (optional) */}
          <div>
            <label htmlFor="favorite-description" className="block text-sm font-medium text-gray-700 mb-1">
              {t('favorites.description', 'Description')} ({t('common.optional', 'optional')})
            </label>
            <textarea
              id="favorite-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('favorites.descriptionPlaceholder', 'Add a note about this meal...')}
              className="w-full px-3 py-2 focus:ring-2 resize-none"
              rows={2}
              maxLength={500}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-surface-container border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={saveMutation.isPending || !name.trim()}
              className="px-4 py-2 text-sm font-medium text-on-neutral bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saveMutation.isPending ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('common.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <StarIcon className="h-4 w-4 mr-1" />
                  {t('favorites.save', 'Save Favorite')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
